import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import type { WorkerStats } from "../api/useWorkerProductivity";

interface WorkerTableProps {
  workers: WorkerStats[];
}

type SortKey = "workerName" | "itemsCompleted" | "avgHandleTimeMinutes" | "utilization";

export function WorkerTable({ workers }: WorkerTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("itemsCompleted");
  const [sortDesc, setSortDesc] = useState(true);

  const sorted = [...workers].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    }
    return sortDesc ? (bVal as number) - (aVal as number) : (aVal as number) - (bVal as number);
  });

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDesc(!sortDesc);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  const sortIcon = (column: SortKey) => {
    if (sortKey !== column) return <ChevronDown className="h-3 w-3 text-gray-300" />;
    return sortDesc
      ? <ChevronDown className="h-3 w-3 text-gray-600 dark:text-gray-300" />
      : <ChevronUp className="h-3 w-3 text-gray-600 dark:text-gray-300" />;
  };

  if (workers.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 bg-gray-50 dark:bg-white/5 rounded-lg">
        <p className="text-sm text-gray-400">No worker data available</p>
      </div>
    );
  }

  const columns: { key: SortKey; label: string; format: (w: WorkerStats) => string }[] = [
    { key: "workerName", label: "Worker", format: (w) => w.workerName },
    { key: "itemsCompleted", label: "Completed", format: (w) => String(w.itemsCompleted) },
    { key: "avgHandleTimeMinutes", label: "Avg Handle Time", format: (w) => `${Math.round(w.avgHandleTimeMinutes)}m` },
    { key: "utilization", label: "Utilization", format: (w) => `${Math.round(w.utilization)}%` },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-white/10">
            {columns.map((col) => (
              <th
                key={col.key}
                className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => toggleSort(col.key)}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {sortIcon(col.key)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((worker) => (
            <tr key={worker.workerId} className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5">
              {columns.map((col) => (
                <td key={col.key} className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  {col.format(worker)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
